/**
 * launchSchemaBinding — read the DECLARED launch contract before rendering it.
 *
 * The reworked mortgagee launch form is schema-derived, not name-derived
 * (PRD AD-4, plan PAD-2). This module turns the catalog entry's `input_schema`
 * into everything the form, the payload builder and the validator need:
 *
 *   - the switch that selects the grouped form over today's flat form,
 *   - the two rendered slices (run level, one action),
 *   - the logical field ROLES resolved to DECLARED key spellings,
 *   - the multi-action gate maximum,
 *   - the allow-list the payload filters against, and
 *   - BOTH loudness lists: a role with no declared key (`missingRoles`) and a
 *     declared key with no control (`unboundRunLevelKeys`).
 *
 * Two rules run through everything here.
 *
 * 1. **Never invent a key.** The launch payload silently discards keys the
 *    contract does not declare — three defects have already been traced to
 *    exactly that — so guessing a spelling is strictly worse than failing. A
 *    role whose candidates all miss lands in `missingRoles` and the form says
 *    so out loud.
 * 2. **Never subtract a key from the payload's allow-list.** The three literal
 *    name lists below (`LAUNCH_ROLE_CANDIDATES`, `HOST_INJECTED_KEYS`,
 *    `TERM_DERIVED_KEYS`) resolve names or suppress a REPORT. None of them can
 *    put a key into a payload, and none of them can take one out of
 *    `declaredKeys`.
 *
 * Pure: no React, no Office.js, no network — so both hosts and jsdom can load it.
 */

import {
  parseObjectSchema,
  resolveNullableAnyOf,
  resolveSchemaRef,
  type FieldSchema,
} from "~/common/workflows/schemaHelpers";

// ---------------------------------------------------------------------------
// Role table
// ---------------------------------------------------------------------------

/**
 * Candidate DECLARED key spellings per logical role, most-preferred first.
 *
 * `action-list-contract` AD-3 states the field names are non-binding, so the
 * form cannot hardcode a spelling. A rename therefore costs ONE line here
 * rather than a rework — and a rename nobody mirrored here fails loudly through
 * `missingRoles` instead of shipping a launch with the value missing.
 *
 * Matching is case-insensitive against the declared keys (see `resolveRole`).
 */
export const LAUNCH_ROLE_CANDIDATES = {
  /** Run level: the array of action objects. Name candidates ONLY — see
   *  `resolveActionsKey` for why there is deliberately no structural fallback. */
  actions: ["actions", "action_list"],
  /** Run level. */
  replyDraft: ["draft_reply", "reply_draft", "create_reply_draft"],
  /** Run level. */
  carrierEvidenceOverride: [
    "carrier_evidence_override",
    "skip_carrier_evidence_wait",
  ],
  /** Per action. */
  changeType: ["change_type"],
  /** Per action. */
  interestType: ["interest_type"],
  /** Per action. */
  targetInterest: ["target_interest"],
  /** Per action. */
  interestRecords: ["interests", "mortgagees"],
  /** Per action. */
  effectiveDate: ["effective_date"],
  /** Per action OR per interest record — PRD OQ-1 leaves the placement to the
   *  contract, so this role is resolved wherever it is declared. */
  locationNumber: ["location_number", "location_no", "location"],
  /**
   * Per action: the term-precise policy identity.
   *
   * These are the four keys `action-list-contract` AD-4 moves INTO the action,
   * under their POST-reshape spellings (`_PREFILL_KEY_RENAMES` renames three of
   * them on the way in). Matching the pre-reshape run-level names here would
   * resolve `termIdentity` to `policy_number` ALONE — a non-empty subset, so the
   * missing-role banner would NOT fire and the launch would go out carrying a
   * bare policy number with no term keys. That is the 2026-08-05 wrong-term
   * defect this story exists to prevent, arriving silently.
   */
  termIdentity: [
    "policy_number",
    "policy_type_code",
    "term_effective_date",
    "term_expiration_date",
  ],
} as const satisfies Record<string, readonly string[]>;

/**
 * Run-level keys the HOST supplies rather than the form.
 *
 * Subtracted from `unboundRunLevelKeys` ONLY. They are declared, they are
 * written (by the drawer), and they have no rendered control by design — so
 * naming them on the form as "declared but unsettable" would be three or six
 * false alarms next to the real one.
 */
export const HOST_INJECTED_KEYS: readonly string[] = [
  "idempotency_key",
  "graph_message_id",
  "client_sk",
  "client_name",
  "oversight_level",
  "use_sp_login",
];

/**
 * Run-level keys the TERM control writes from the selected option row.
 *
 * Today's flat schema declares `policy_type`, `carrier_name` and `carrier_id` at
 * run level and today's form renders them DISABLED while the term picker writes
 * them (`App.tsx` fieldDisabled + the term-select handler). If the reshaped
 * contract keeps them run-level they DO have a writer and a control, so listing
 * them as unbound would show a loud error banner for three fields that are
 * working correctly.
 *
 * Named explicitly rather than pattern-matched, so a genuinely NEW run-level
 * property still lands in `unboundRunLevelKeys`.
 */
export const TERM_DERIVED_KEYS: readonly string[] = [
  "policy_type",
  "carrier_name",
  "carrier_id",
];

/**
 * Run-level keys written by a control that renders INSIDE the action card.
 *
 * `old_mortgagee_name` is declared run level (the reshaped contract keeps it on
 * the envelope) but the control that writes it — `OldMortgageeSelect` — belongs
 * beside the `target_interest` it drives, which is per action. So the key HAS a
 * control; it is simply not in the run-level region. Reporting it as unbound
 * would be a loud banner for a field that works, which is the same false alarm
 * `TERM_DERIVED_KEYS` exists to prevent.
 *
 * Deliberately NOT a role: the payload is complete without this key, so a role
 * would put it in `missingRoles` — a different loud banner — for every contract
 * that omits it. `ActionCard.OLD_INTEREST_NAME_KEY` carries the same literal and
 * is the control side of this pairing; changing one without the other is how the
 * key becomes silently unsettable again.
 *
 * Named explicitly rather than pattern-matched, so a genuinely NEW run-level
 * property still lands in `unboundRunLevelKeys`.
 */
export const CARD_RENDERED_RUN_LEVEL_KEYS: readonly string[] = [
  "old_mortgagee_name",
];

/**
 * Roles that resolve when present and stay silent when absent.
 *
 * `locationNumber` is PRD OQ-1: the form follows the contract's placement, and
 * the published v2 schema does not declare it. A missing optional role is
 * not a broken contract.
 */
export const OPTIONAL_LAUNCH_ROLES: ReadonlySet<string> = new Set([
  "locationNumber",
]);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LaunchBindingRoles {
  replyDraft: string | null;
  carrierEvidenceOverride: string | null;
  changeType: string | null;
  interestType: string | null;
  targetInterest: string | null;
  interestRecords: string | null;
  effectiveDate: string | null;
  /** Declared per action OR per interest record (OQ-1). */
  locationNumber: string | null;
  /** The declared SUBSET of the four term keys, in candidate order. */
  termIdentity: string[];
}

export interface LaunchBinding {
  /** Present only when the schema declares an action list — the switch that
   *  selects the grouped form over today's flat form. */
  isReshaped: boolean;
  /** The declared key of the action list (role: actions). */
  actionsKey: string | null;
  /** JSON-Schema slice for ONE action (`schema.properties[actionsKey].items`,
   *  `$ref` resolved, carrying the root's `$defs` so nested `$ref`s resolve). */
  actionItemSchema: Record<string, unknown> | null;
  /** JSON-Schema slice for the run level: every declared property EXCEPT the
   *  action list. */
  runLevelSchema: Record<string, unknown> | null;
  /** Roles resolved to DECLARED keys. A null value means the schema declares no
   *  candidate — the caller must surface it, never invent one. */
  roles: LaunchBindingRoles;
  /** Declared enumeration for interest type, in schema order. */
  interestTypeValues: string[];
  /** Gate maximum from `maxItems` on the actions array; 1 when absent (PAD-3). */
  maxActions: number;
  /** Every key the schema declares, run level + per action + per record — the
   *  allow-list the payload builder filters against (AC-6c). `x-hidden`
   *  properties ARE included: they are declared, and the drawer injects some of
   *  them (notably the `x-hidden` term pair added on 2026-08-05). */
  declaredKeys: {
    runLevel: Set<string>;
    action: Set<string>;
    interestRecord: Set<string>;
  };
  /** Roles the schema failed to declare — rendered as a loud form-level error. */
  missingRoles: string[];
  /** The mirror of `missingRoles`: declared RUN-LEVEL properties that no role
   *  resolves to, are not `x-hidden`, and are none of `HOST_INJECTED_KEYS`,
   *  `TERM_DERIVED_KEYS` or `CARD_RENDERED_RUN_LEVEL_KEYS`. Nothing else renders these, so without this list a
   *  newly declared run-level property would be silently unsettable and would
   *  never reach the payload — the invisible-omission class AC-6a exists to
   *  catch. A REPORT, never a filter: an unbound key stays in
   *  `declaredKeys.runLevel` so a host that does supply it still gets it through. */
  unboundRunLevelKeys: string[];
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function propertiesOf(
  schema: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  return asRecord(schema?.properties) ?? {};
}

/** Resolve ONE role against a set of declared keys, case-insensitively.
 *  Returns the DECLARED spelling (not the candidate) so callers index with it. */
function resolveRole(
  candidates: readonly string[],
  declared: readonly string[],
): string | null {
  const byLower = new Map(declared.map((k) => [k.toLowerCase(), k]));
  for (const candidate of candidates) {
    const hit = byLower.get(candidate.toLowerCase());
    if (hit) return hit;
  }
  return null;
}

/** Every declared spelling among `candidates`, in candidate order. */
function resolveRoleSubset(
  candidates: readonly string[],
  declared: readonly string[],
): string[] {
  const byLower = new Map(declared.map((k) => [k.toLowerCase(), k]));
  const hits: string[] = [];
  for (const candidate of candidates) {
    const hit = byLower.get(candidate.toLowerCase());
    if (hit) hits.push(hit);
  }
  return hits;
}

/**
 * The action list is found by NAME only — deliberately.
 *
 * A structural "the one run-level property that is an array of objects" rule
 * would match today's FLAT schema, whose run-level `mortgagees` is exactly
 * that, and `isReshaped` would become true for `mortgagee_update_v1_2`. The
 * pilot's live launch path would then render the grouped form. Failing to
 * recognise a renamed action list is a loud banner; mis-recognising a flat
 * schema is a broken live workflow.
 */
function resolveActionsKey(
  props: Record<string, unknown>,
): { key: string; def: Record<string, unknown> } | null {
  const key = resolveRole(LAUNCH_ROLE_CANDIDATES.actions, Object.keys(props));
  if (!key) return null;
  const raw = asRecord(props[key]);
  if (!raw) return null;
  const def = resolveNullableAnyOf(raw) ?? raw;
  // Must actually be a list. `type: "multiline"` is the substrate's ui-hint on
  // a list field (see `parseObjectSchema`), so both spellings count.
  const isList =
    (def.type === "array" || def.type === "multiline") && !!def.items;
  return isList ? { key, def } : null;
}

/**
 * The item schema for a list property, `$ref` resolved and re-rooted.
 *
 * Re-rooting matters: `MortgageeAction.target_interest` and
 * `MortgageeAction.mortgagees` are themselves `$ref`s into the ROOT's `$defs`.
 * An item schema handed on without `$defs` resolves those to `undefined` and the
 * AM silently loses the record editor to an "unknown field" JSON textarea.
 */
function itemSchemaOf(
  root: Record<string, unknown> | null | undefined,
  listDef: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  const items = asRecord(listDef?.items);
  if (!items) return null;
  const resolved = resolveSchemaRef(root, items) ?? items;
  const target = asRecord(resolved);
  if (!target) return null;
  const defs = root?.["$defs"];
  return defs !== undefined && target["$defs"] === undefined
    ? { ...target, $defs: defs }
    : { ...target };
}

/** `maxItems` off the declared list, floored at 1 (PAD-3: absent == closed). */
function readMaxActions(listDef: Record<string, unknown> | null): number {
  const raw = listDef?.maxItems;
  const n = typeof raw === "number" && Number.isFinite(raw) ? raw : 1;
  return Math.max(1, Math.floor(n));
}

/** The declared enum on a property, after unwrapping `Optional[X]`. */
function enumValuesOf(
  props: Record<string, unknown>,
  key: string | null,
): string[] {
  if (!key) return [];
  const raw = asRecord(props[key]);
  if (!raw) return [];
  const def = resolveNullableAnyOf(raw) ?? raw;
  return Array.isArray(def.enum) ? def.enum.map((v) => String(v)) : [];
}

function isHidden(props: Record<string, unknown>, key: string): boolean {
  return asRecord(props[key])?.["x-hidden"] === true;
}

/**
 * The binding for a schema that declares no action list — a FACTORY, not a
 * shared constant, so two flat bindings can never alias one another's Sets.
 *
 * `missingRoles` is deliberately EMPTY here rather than "every role": a flat
 * schema is not a broken reshaped one, and the form must not shout at
 * `mortgagee_update_v1_2`. `isReshaped: false` is the whole signal.
 */
function flatBinding(): LaunchBinding {
  return {
    isReshaped: false,
    actionsKey: null,
    actionItemSchema: null,
    runLevelSchema: null,
    roles: {
      replyDraft: null,
      carrierEvidenceOverride: null,
      changeType: null,
      interestType: null,
      targetInterest: null,
      interestRecords: null,
      effectiveDate: null,
      locationNumber: null,
      termIdentity: [],
    },
    interestTypeValues: [],
    maxActions: 1,
    declaredKeys: freshKeys(),
    missingRoles: [],
    unboundRunLevelKeys: [],
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Read a declared launch schema into a `LaunchBinding`.
 *
 * Total: never throws. A schema that declares no action list yields
 * `isReshaped: false` and empty role/key sets, so today's flat form keeps
 * rendering for `mortgagee_update_v1_2` and every non-mortgagee workflow — this
 * module can never block the pilot's live launch path.
 */
export function buildLaunchBinding(
  schema: Record<string, unknown> | null | undefined,
): LaunchBinding {
  const root = asRecord(schema);
  if (!root) return flatBinding();

  const rootProps = propertiesOf(root);
  const list = resolveActionsKey(rootProps);
  if (!list) return flatBinding();

  const actionsKey = list.key;
  const actionItemSchema = itemSchemaOf(root, list.def);
  const actionProps = propertiesOf(actionItemSchema);

  // Run level = every declared property EXCEPT the action list itself. The
  // action list is excluded from `declaredKeys.runLevel` on purpose: the payload
  // builder writes `inputs[actionsKey]` from the BUILT actions, so leaving the
  // key in the run-level allow-list would let a raw `values.actions` through
  // beside it.
  const runLevelKeys = Object.keys(rootProps).filter((k) => k !== actionsKey);
  const runLevelRequired = Array.isArray(root.required)
    ? (root.required as string[]).filter((k) => k !== actionsKey)
    : [];
  const runLevelProps: Record<string, unknown> = {};
  for (const key of runLevelKeys) runLevelProps[key] = rootProps[key];
  const runLevelSchema: Record<string, unknown> = {
    type: "object",
    properties: runLevelProps,
    required: runLevelRequired,
    ...(root["$defs"] !== undefined ? { $defs: root["$defs"] } : {}),
  };

  // Roles. Run-level roles resolve against the run-level keys, per-action roles
  // against the action item's keys.
  const interestRecords = resolveRole(
    LAUNCH_ROLE_CANDIDATES.interestRecords,
    Object.keys(actionProps),
  );
  const recordItemSchema = interestRecords
    ? itemSchemaOf(
        actionItemSchema ?? root,
        (() => {
          const raw = asRecord(actionProps[interestRecords]);
          return raw ? (resolveNullableAnyOf(raw) ?? raw) : null;
        })(),
      )
    : null;
  const recordProps = propertiesOf(recordItemSchema);

  const roles: LaunchBindingRoles = {
    replyDraft: resolveRole(LAUNCH_ROLE_CANDIDATES.replyDraft, runLevelKeys),
    carrierEvidenceOverride: resolveRole(
      LAUNCH_ROLE_CANDIDATES.carrierEvidenceOverride,
      runLevelKeys,
    ),
    changeType: resolveRole(
      LAUNCH_ROLE_CANDIDATES.changeType,
      Object.keys(actionProps),
    ),
    interestType: resolveRole(
      LAUNCH_ROLE_CANDIDATES.interestType,
      Object.keys(actionProps),
    ),
    targetInterest: resolveRole(
      LAUNCH_ROLE_CANDIDATES.targetInterest,
      Object.keys(actionProps),
    ),
    interestRecords,
    effectiveDate: resolveRole(
      LAUNCH_ROLE_CANDIDATES.effectiveDate,
      Object.keys(actionProps),
    ),
    // OQ-1: per action OR per record — resolved wherever the contract declares
    // it, action level first.
    locationNumber:
      resolveRole(
        LAUNCH_ROLE_CANDIDATES.locationNumber,
        Object.keys(actionProps),
      ) ??
      resolveRole(
        LAUNCH_ROLE_CANDIDATES.locationNumber,
        Object.keys(recordProps),
      ),
    termIdentity: resolveRoleSubset(
      LAUNCH_ROLE_CANDIDATES.termIdentity,
      Object.keys(actionProps),
    ),
  };

  const missingRoles: string[] = [];
  for (const [role, value] of Object.entries(roles)) {
    const missing = Array.isArray(value) ? value.length === 0 : value === null;
    // locationNumber is OQ-1 optional: the published contract does not declare
    // it, and shouting+blocking submit for a field the form is not rendering
    // is how the pane looked broken when the grouped form first mounted.
    if (missing && !OPTIONAL_LAUNCH_ROLES.has(role)) missingRoles.push(role);
  }

  const roleResolvedRunLevel = new Set(
    [roles.replyDraft, roles.carrierEvidenceOverride].filter(
      (k): k is string => !!k,
    ),
  );
  const suppressed = new Set<string>([
    ...HOST_INJECTED_KEYS,
    ...TERM_DERIVED_KEYS,
    ...CARD_RENDERED_RUN_LEVEL_KEYS,
  ]);
  const unboundRunLevelKeys = runLevelKeys.filter(
    (key) =>
      !roleResolvedRunLevel.has(key) &&
      !isHidden(rootProps, key) &&
      !suppressed.has(key),
  );

  return {
    isReshaped: true,
    actionsKey,
    actionItemSchema,
    runLevelSchema,
    roles,
    interestTypeValues: enumValuesOf(actionProps, roles.interestType),
    maxActions: readMaxActions(list.def),
    declaredKeys: {
      runLevel: new Set(runLevelKeys),
      action: new Set(Object.keys(actionProps)),
      interestRecord: new Set(Object.keys(recordProps)),
    },
    missingRoles,
    unboundRunLevelKeys,
  };
}

function freshKeys(): LaunchBinding["declaredKeys"] {
  return {
    runLevel: new Set<string>(),
    action: new Set<string>(),
    interestRecord: new Set<string>(),
  };
}

/**
 * Split ONE flat values record into the two slices the payload builder and the
 * validator take.
 *
 * `actions` is `values[actionsKey]` (`[]` when absent, non-array, or holding
 * non-objects); `runLevelValues` is everything else. This is the ONLY place the
 * split is defined, so the drawer's `handleLaunch` (which receives one values
 * record) and its `handleRetry` (which receives none and reads the drawer's own
 * `values` state) cannot disagree about what an action is.
 *
 * Total: never throws, and never invents an action.
 */
export function splitLaunchValues(
  binding: LaunchBinding,
  values: Record<string, unknown> | null | undefined,
): {
  runLevelValues: Record<string, unknown>;
  actions: Record<string, unknown>[];
} {
  const source = asRecord(values) ?? {};
  const key = binding.actionsKey;
  if (!key) return { runLevelValues: { ...source }, actions: [] };

  const runLevelValues: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(source)) {
    if (k !== key) runLevelValues[k] = v;
  }

  const raw = source[key];
  const actions = Array.isArray(raw)
    ? raw
        .map((entry) => asRecord(entry))
        .filter((entry): entry is Record<string, unknown> => entry !== null)
    : [];

  return { runLevelValues, actions };
}

/**
 * Rebuild the ONE flat record today's launch helpers expect.
 *
 * After `LaunchForm` normalisation the term identity, effective date, target
 * interest and interest records live on the ACTION, while the prefill echoes
 * (`policy_options`, `interest_options`, `interest_options_by_line`,
 * `interest_options_line_sk`, `policy_term_key`) stay at run level. Every helper
 * lifted in Phase 1 — `readInterestOptionsForSelection`, `interestResetPatch`,
 * `interestAutoPickPatch`, `readPolicyTermKey`, `readPolicyOptions`,
 * `findPolicyOption`, `validateEffectiveDateAgainstPolicy` — reads BOTH halves
 * off ONE record, so it must be fed this merge. Handing one of them the action
 * slice alone silently no-ops (`resolveSelectedPolicy` returns null on a missing
 * policy number; `readInterestOptionsForSelection` returns an empty list and the
 * target-interest select renders blank).
 *
 * ⚠️ READ VIEW ONLY. Never spread the result back into the action: that copies
 * the run-level prefill echoes onto the action, where they are either shipped or
 * reported as dropped on every launch.
 *
 * Action keys overlay run-level keys. `binding.actionsKey` is stripped if
 * present (`splitLaunchValues` already removed it; this defends a caller that
 * passed the unsplit record). An undefined or empty action yields the run-level
 * keys alone — this never invents an action.
 */
export function mergeLaunchSlice(
  binding: LaunchBinding,
  runLevelValues: Record<string, unknown> | null | undefined,
  action: Record<string, unknown> | undefined | null,
): Record<string, unknown> {
  const merged: Record<string, unknown> = {
    ...(asRecord(runLevelValues) ?? {}),
    ...(asRecord(action) ?? {}),
  };
  if (binding.actionsKey && binding.actionsKey in merged) {
    delete merged[binding.actionsKey];
  }
  return merged;
}

/** Parsed field list for ONE action — the slice `ActionCard` renders.
 *  `x-hidden` properties are excluded (that is `parseObjectSchema`'s contract);
 *  they remain in `declaredKeys.action` so the payload still carries them. */
export function actionFields(binding: LaunchBinding): FieldSchema[] {
  return parseObjectSchema(binding.actionItemSchema) ?? [];
}

/** Parsed field list for the run level — the slice the form's run-level region
 *  renders around the three role-resolved controls. */
export function runLevelFields(binding: LaunchBinding): FieldSchema[] {
  return parseObjectSchema(binding.runLevelSchema) ?? [];
}
